import { createReducer, Action, on } from '@ngrx/store';

import { AppActions } from './app.actions';
import { AppState } from './app.state';
import { HelpComponentMode } from '../../features/help/models/help.model';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { UserProfileActions } from '../user-profile/user-profile.actions';

export const initialState: AppState = {
  printMode: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(AppActions.LayoutChanged, (state, action): AppState => ({ ...state, breakpoint: action.breakpoint })),
    on(
      AppActions.SetExtended,
      (state, action): AppState => ({
        ...state,
        sidenavExtended: action.sidenavExtended ? 'open' : 'closed',
      }),
    ),
    on(AppActions.SetUrl, (state, action): AppState => ({ ...state, url: action.url })),
    on(
      AppActions.LoadInitDataSuccess,
      (state, action): AppState => ({
        ...state,
        libraries: action.data.libraries,
        availableAssets: action.data.assets || [],
        availableWorkspaces: action.data.workspaces || [],
        assetWorkspaceIds: action.data.assetWorkspaceIds ?? [],
        workspaceGatewayUnavailable: action.data.workspaceGatewayUnavailable === true,
        initialized: true,
        appEnabled: true,
      }),
    ),
    on(
      AppActions.RefreshWorkspacesSuccess,
      (state, action): AppState => ({
        ...state,
        availableWorkspaces: action.workspaces,
        // The gateway just answered, so a boot-time failure banner is no longer true.
        workspaceGatewayUnavailable: false,
      }),
    ),
    on(AppActions.LoadInitData, (state): AppState => ({ ...state, libraries: undefined })),
    on(
      AppActions.LoadBuiltInChecklistsSuccess,
      (state, action): AppState => ({
        ...state,
        builtInChecklistTopicIdsByAsset: action.topicIdsByAsset,
      }),
    ),
    on(AppActions.EnterOfflineModeSuccess, (state): AppState => ({ ...state, isOffline: true })),
    on(AppActions.RestoreFromOfflineMode, (state): AppState => ({ ...state, isOffline: false })),
    on(
      UserProfileActions.LoadProfileSuccess,
      (state, action): AppState => ({
        ...state,
        selectedAssetShortNames: (action.init ? action.selectedAssetShortName : state.selectedAssetShortNames) || [],
        availableAssets:
          action.init && !!action.selectedAssetShortName && !!action.profile?.permissions?.length ? state.availableAssets : [],
        selectedWorkspaceId: action.claimedWorkspaceId ?? action.profile?.selectedWorkspaceId ?? state.selectedWorkspaceId,
        appEnabled: true,
      }),
    ),
    on(
      UserProfileActions.SetWorkspaceSuccess,
      (state, action): AppState => ({
        ...state,
        selectedWorkspaceId: action.workspaceId,
        // The chosen workspace's assets ARE the new selection, so the store (and everything
        // that reads it — *hasAsset, dashboard, X-Semeris-Asset header) reflects the new
        // scope instead of the stale prior selection.
        selectedAssetShortNames: action.assetShortNames || [],
      }),
    ),
    on(AppActions.SetAppEnabled, (state, action): AppState => ({ ...state, appEnabled: action.appEnabled })),
    on(
      AppActions.SetPrintMode,
      (state, action): AppState => ({
        ...state,
        printMode: action.isPrintMode,
        printDate: action.isPrintMode ? new Date() : undefined,
      }),
    ),
    on(AppActions.SetSystemPrefersDark, (state, action): AppState => ({ ...state, systemPrefersDark: action.prefersDark })),
    on(AppActions.ToggleNotesPanel, (state, action): AppState => {
      const notesPanelOpen = action.open !== undefined ? action.open : !state.notesPanelOpen;
      return {
        ...state,
        notesPanelOpen,
        // Notes and help share the same slot region — one open closes the other.
        helpOpen: notesPanelOpen ? false : state.helpOpen,
      };
    }),
    on(
      AppActions.ToggleHelp,
      (state, action): AppState => ({
        ...state,
        helpOpen: action.open,
        helpId: action.helpId || state.helpId,
        helpContextual: action.contextual ?? state.helpContextual ?? true,
        // Falls back to Help, NOT to the previous mode.
        helpMode: action.helpMode ?? HelpComponentMode.Help,
        notesPanelOpen: action.open ? false : state.notesPanelOpen,
      }),
    ),
    on(AppActions.SetHelpId, (state, action): AppState => ({ ...state, helpId: action.helpId })),
    on(AppActions.SetContextualHelp, (state, action): AppState => ({ ...state, helpContextual: action.enabled })),
    on(
      AppActions.LoadSynonymClassesSuccess,
      (state, action): AppState => ({ ...state, synonymClassesByAsset: action.synonymClassesByAsset }),
    ),
    on(AppActions.LoadSystemMessagesSuccess, (state, action): AppState => ({ ...state, systemMessages: action.systemMessages })),
  ),
);

export function reducer(state: AppState | undefined, action: Action) {
  return featureReducer(state, action);
}
